'use client';

import { useRef } from 'react';
import type { LinkProps } from 'next/link';
import { useTranslations } from 'next-intl';
import CustomLink from 'components/shared/CustomLink';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import { hasMinorUnit, useFormatPrice } from 'hooks/pricing/useFormattedPrice';
import { getCurrencySymbolFromEnum } from 'lib/region/countryUtils';
import { Currency } from 'utils/Currency';
import { useRevealOnce } from './hooks/useRevealOnce';
import styles from './AffiliateProductCard.module.css';

const DISCARD_PAGE_PARAMS = ['discount'];

const roundToMinorUnit = (value: number, currency: Currency): number =>
  hasMinorUnit(currency) ? Math.round(value * 100) / 100 : Math.round(value);

// Shopify's generated codes (`fcoup_…`) are not something to print under a price.
const isDisplayCode = (code: string): boolean =>
  !/^fcoup_/i.test(code) && code.length <= 20;

export type AffiliateProductCardProps = {
  title: string;
  description: string;
  imageSrc: string;
  imageAlt: string;
  /** `render`: product render; `photo`: cover crop; `contain`: whole image, padded. */
  imageFit?: 'render' | 'photo' | 'contain';
  imagePosition?: string;
  /** Vertical nudge for a render, in px (prototype `--dy`). */
  imageOffsetY?: number;
  /** Phone heading row ("Ring PRO") above the card; it rises in with the card. */
  introLabel?: string;
  /** Phone carousel footer: price row, then the applied line beside Buy now. */
  inCarousel?: boolean;
  price: number;
  currency: Currency;
  /** "From ₹4,699" for products with several plans. */
  fromPrice?: boolean;
  /** Struck base price, discounted price and "{n}% off" badge. */
  discountPercentage?: number;
  /** "{code} applied" under the price. */
  code?: string | null;
  /** "Tax incl." / "Tax excl." for the store. */
  taxLabel: string;
  /** Buy now link; carries the code already. */
  href: LinkProps['href'];
  priority?: boolean;
  className?: string;
  /** Rise-and-fade entrance on first scroll into view (ring cards only). */
  entrance?: boolean;
  delayMs?: number;
};

// v3 card from the affiliate invite prototype: glass tile, price, "{code} applied", Buy now link.
export const AffiliateProductCard = ({
  title,
  description,
  imageSrc,
  imageAlt,
  imageFit = 'render',
  imagePosition,
  imageOffsetY,
  introLabel,
  inCarousel = false,
  price,
  currency,
  fromPrice = false,
  discountPercentage,
  code,
  taxLabel,
  href,
  priority = false,
  className,
  entrance = false,
  delayMs = 0,
}: AffiliateProductCardProps) => {
  const t = useTranslations('invite');
  const tCart = useTranslations('cart');
  const formatPrice = useFormatPrice();
  const ref = useRef<HTMLElement>(null);
  const revealed = useRevealOnce(ref, entrance);

  const symbol = getCurrencySymbolFromEnum(currency);
  const hasDiscount =
    typeof discountPercentage === 'number' &&
    discountPercentage > 0 &&
    discountPercentage < 100;
  // Round to the currency's minor unit, as checkout does when it applies the code.
  const finalPrice = hasDiscount
    ? roundToMinorUnit(price * (1 - discountPercentage / 100), currency)
    : price;
  const priceText = `${symbol}${formatPrice(finalPrice)}`;
  // A page-level `discount` (from the landing URL) must not ride onto the buy
  // link; the card's own code goes in the href query.
  const linkProps = { href, discardParams: DISCARD_PAGE_PARAMS } as const;

  const reveal = [
    entrance ? styles.entrance : '',
    entrance && revealed ? styles.in : '',
  ];
  const delayStyle = entrance
    ? ({ '--d': `${delayMs}ms` } as React.CSSProperties)
    : undefined;
  const classes = [
    styles.card,
    ...reveal,
    inCarousel ? styles.inCarousel : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  const imageStyle: React.CSSProperties = {};
  if (imagePosition) imageStyle.objectPosition = imagePosition;
  if (typeof imageOffsetY === 'number') {
    (imageStyle as Record<string, string>)['--dy'] = `${imageOffsetY}px`;
  }

  return (
    <>
      {introLabel ? (
        <div
          className={[styles.intro, ...reveal].filter(Boolean).join(' ')}
          style={delayStyle}
        >
          <h2>{introLabel}</h2>
        </div>
      ) : null}
      <article ref={ref} className={classes} style={delayStyle}>
        <div className={styles.mediaWrap}>
          <CustomLink
            {...linkProps}
            className={[
              styles.media,
              imageFit !== 'render' ? styles.isPhoto : '',
              imageFit === 'contain' ? styles.isContain : '',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-label={imageAlt}
          >
            <CustomImage
              src={imageSrc}
              alt={imageAlt}
              width={364}
              height={335}
              sizes="(min-width: 761px) 364px, 89vw"
              priority={priority}
              style={Object.keys(imageStyle).length ? imageStyle : undefined}
            />
          </CustomLink>
        </div>
        <div className={styles.body}>
          <div className={styles.text}>
            <h2>{title}</h2>
            <p>{description}</p>
          </div>
          <div className={styles.foot}>
            <div className={styles.price}>
              <div className={styles.priceRow}>
                {hasDiscount ? (
                  <s>
                    {symbol}
                    {formatPrice(price)}
                  </s>
                ) : null}
                <strong>
                  {fromPrice ? t('card.from', { price: priceText }) : priceText}
                </strong>
                <span>{taxLabel}</span>
              </div>
              {code && isDisplayCode(code) ? (
                <span className={styles.applied}>
                  {t('card.applied', { code })}
                </span>
              ) : null}
            </div>
            <CustomLink
              {...linkProps}
              className={styles.cta}
              data-product={title}
            >
              {tCart('sharedCart.upsell.buyNow')}
            </CustomLink>
          </div>
        </div>
        {hasDiscount ? (
          <span className={styles.badge}>
            {t('card.off', { percent: discountPercentage })}
          </span>
        ) : null}
      </article>
    </>
  );
};
